import { Breadcrumb } from 'antd'
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb'
import Link from 'next/link'
import { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import PlatformSwitcher from '@/components/common/platform-switcher'
import useUrlControl from '@/components/public-page/hook/use-url-control'
import routes from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import { PlatformCode } from '@/types/schema/platform/platform-code-schema'
import { PlatformShortcode } from '@/utils/convert-platform'
import { convertPlatformCodeFromZodPlatformCode } from '@/utils/platform/convert-platform-code-from-zod-platform-code'

interface HeaderProps {
  pageTitle: string
  breadcrumbItems?: ItemType[]
  onChangePlatform?: (platform: PlatformCode) => void
}

const Header: FunctionComponent<HeaderProps> = ({
  pageTitle,
  breadcrumbItems,
  onChangePlatform,
}) => {
  const { formatMessage } = useIntl()
  const { getRootUrl } = useUrlControl()
  const params = routes.billboardCustomizedTagList.useParams()

  return (
    <Wrapper>
      <HeaderWrapper hasPlatformSwitcher={!!onChangePlatform}>
        <Breadcrumb
          itemRender={(route): ReactNode => {
            if (route.href === getRootUrl()) {
              return <a href={route.href}>{route.title}</a>
            }

            return route.href ? (
              <Link href={route.href}>{route.title}</Link>
            ) : (
              <span>{route.title}</span>
            )
          }}
          items={[
            {
              href: getRootUrl(),
              title: formatMessage({ id: 'irm:header_button_homepage' }),
            },
            ...(breadcrumbItems ?? []),
          ]}
        />
        {onChangePlatform && (
          <PlatformSwitcher
            value={
              params.data?.platform
                ? (convertPlatformCodeFromZodPlatformCode(params.data.platform)
                    ?.shortcode as PlatformShortcode)
                : undefined
            }
            onChange={(platform): void => onChangePlatform(platform.code)}
          />
        )}
      </HeaderWrapper>
      <PageTitle>{pageTitle}</PageTitle>
    </Wrapper>
  )
}

export default Header

const HeaderWrapper = styled.div<{ hasPlatformSwitcher?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: ${({ hasPlatformSwitcher }): string =>
    hasPlatformSwitcher ? 'space-between' : 'flex-start'};
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 24px;
    .ant-breadcrumb ol {
      justify-content: center;
    }
  }
`

const PageTitle = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 36px;
  font-weight: 700;
  line-height: 140%;
  margin: 40px auto;

  @media (max-width: 768px) {
    font-size: 24px;
    margin: 24px auto;
    text-align: center;
  }
`
const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`
